import { useState, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useCustomer } from '../../lib/customer';
import { theme } from '../../styles/theme';
import { Logo } from '../../components/Logo';
import { Seo } from '../../lib/seo';

import { PortalPanel } from './tabs/Panel';
import { PortalCotizaciones } from './tabs/Cotizaciones';
import { PortalProyectos } from './tabs/Proyectos';
import { PortalPrecios } from './tabs/Precios';
import { PortalPedidos } from './tabs/Pedidos';
import { PortalCapacitacion } from './tabs/Capacitacion';
import { PortalCuenta } from './tabs/Cuenta';

export type PortalTab = 'panel' | 'cotizaciones' | 'proyectos' | 'precios' | 'pedidos' | 'capacitacion' | 'cuenta';

export function PortalShell() {
  const { customer } = useCustomer();
  const { signOut } = useAuth();
  const [tab, setTab] = useState<PortalTab>('panel');
  const navigate = useNavigate();

  if (!customer) return null;

  const margin = customer.tier_discount_pct ?? 0;

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: theme.bgSoft,
      color: theme.text,
      fontFamily: theme.font.sans,
    }}>
      <Seo title={`Portal · ${customer.business_name}`} description="Portal NULED para distribuidores e integradores" />

      {/* Sidebar */}
      <aside style={{
        width: 260,
        background: theme.brand,
        color: theme.textOnBrand,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}>
        <div style={{ padding: '24px 20px 20px' }}>
          <Link to="/" style={{ color: theme.textOnBrand, display: 'flex' }}>
            <Logo height={30} />
          </Link>
        </div>

        <div style={{ padding: '12px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: 11, color: theme.accent, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>
            {customer.tier_name ?? 'Distribuidor'}
          </div>
          <div style={{ fontFamily: theme.font.head, fontSize: 17, fontWeight: 600, marginTop: 4, lineHeight: 1.2 }}>
            {customer.business_name}
          </div>
          {customer.rfc && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 4, fontFamily: theme.font.mono }}>
              {customer.rfc}
            </div>
          )}
          {margin > 0 && (
            <div style={{
              marginTop: 12,
              padding: '6px 10px',
              background: 'rgba(87,255,154,0.12)',
              border: `1px solid ${theme.accent}`,
              display: 'inline-block',
              fontSize: 11,
              fontWeight: 700,
              color: theme.accent,
              letterSpacing: 0.5,
            }}>
              Margen {margin}% sobre PVP
            </div>
          )}
        </div>

        <nav style={{ padding: 8, flex: 1, overflowY: 'auto' }}>
          <NavItem icon="📊" label="Panel"        active={tab === 'panel'}        onClick={() => setTab('panel')} />
          <NavItem icon="📝" label="Cotizaciones" active={tab === 'cotizaciones'} onClick={() => setTab('cotizaciones')} />
          <NavItem icon="🏛"  label="Mis proyectos" active={tab === 'proyectos'}  onClick={() => setTab('proyectos')} />
          <NavItem icon="🏷"  label="Mis precios"   active={tab === 'precios'}    onClick={() => setTab('precios')} />
          <NavItem icon="📦" label="Pedidos"       active={tab === 'pedidos'}    onClick={() => setTab('pedidos')} />
          <NavItem icon="🎓" label="Capacitación"  active={tab === 'capacitacion'} onClick={() => setTab('capacitacion')} />
          <NavItem icon="⚙"  label="Cuenta"        active={tab === 'cuenta'}     onClick={() => setTab('cuenta')} />
        </nav>

        <div style={{ padding: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Link to="/" style={{
            display: 'block',
            color: 'rgba(255,255,255,0.55)',
            fontSize: 12,
            textDecoration: 'none',
            marginBottom: 8,
          }}>
            ← Sitio público
          </Link>
          <button
            onClick={async () => { await signOut(); navigate('/'); }}
            style={{
              width: '100%',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.3)',
              color: theme.textOnBrand,
              padding: '8px 12px',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              cursor: 'pointer',
              fontFamily: theme.font.sans,
            }}
          >
            Cerrar sesión
          </button>
        </div>
      </aside>

      <main style={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
        {tab === 'panel'        && <PortalPanel onTab={setTab} />}
        {tab === 'cotizaciones' && <PortalCotizaciones />}
        {tab === 'proyectos'    && <PortalProyectos />}
        {tab === 'precios'      && <PortalPrecios />}
        {tab === 'pedidos'      && <PortalPedidos />}
        {tab === 'capacitacion' && <PortalCapacitacion />}
        {tab === 'cuenta'       && <PortalCuenta />}
      </main>
    </div>
  );
}

function NavItem({ icon, label, active, onClick }: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '11px 12px',
      background: active ? theme.accent : 'transparent',
      color: active ? theme.brand : theme.textOnBrand,
      border: 'none',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      textAlign: 'left',
      borderRadius: 0,
      marginBottom: 2,
    }}>
      <span style={{ fontSize: 14, opacity: 0.85 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
}

// ============= Helpers compartidos por todas las tabs =============

export function PortalContainer({ title, eyebrow, children }: { title: string; eyebrow: string; children: ReactNode }) {
  return (
    <div style={{ padding: '32px 32px 64px', maxWidth: 1240 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontSize: 11,
          color: theme.accentDark,
          textTransform: 'uppercase',
          letterSpacing: 2,
          fontWeight: 700,
        }}>
          {eyebrow}
        </div>
        <h1 style={{
          fontFamily: theme.font.head,
          fontSize: 32,
          fontWeight: 400,
          color: theme.brand,
          margin: '4px 0 0',
        }}>
          {title}
        </h1>
      </div>
      {children}
    </div>
  );
}

export function ComingSoon({ what }: { what: string }) {
  return (
    <div style={{
      padding: 40,
      background: theme.bg,
      border: `1px dashed ${theme.border}`,
      textAlign: 'center',
      color: theme.textMuted,
    }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🚧</div>
      <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, fontWeight: 500, marginBottom: 4 }}>
        {what} · construyendo
      </div>
      <div style={{ fontSize: 13 }}>
        Esta sección llega en los próximos commits. Iteramos en producción.
      </div>
    </div>
  );
}
